import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { X } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, Sector
} from 'recharts';

const fmt = (n) => parseFloat(n||0).toLocaleString('es-EC',{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtK = (n) => {
  const v = parseFloat(n||0);
  return v >= 1000 ? `$${(v/1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
};

// Paleta de colores para empresas/asesores
const PALETTE = [
  '#7B0D0D','#1d4ed8','#15803d','#92400e','#6d28d9',
  '#0e7490','#be185d','#b45309','#065f46','#1e3a5f',
  '#7c2d12','#134e4a','#3730a3','#701a75','#78350f',
];

// ── CUSTOM PIE TOOLTIP ────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div style={{ background:'white', border:`2px solid ${d.payload.color||d.fill}`, borderRadius:10, padding:'12px 16px', boxShadow:'0 4px 20px rgba(0,0,0,0.15)', maxWidth:300 }}>
      <div style={{ fontWeight:700, color:d.payload.color||d.fill, marginBottom:8, fontSize:14 }}>{d.payload.name}</div>
      <div style={{ fontSize:13, color:'#333', marginBottom:6 }}>{d.payload.mainValue}</div>
      {d.payload.listaProyectos?.slice(0,8).map((p,i) => (
        <div key={i} style={{ fontSize:11, color:'#666', padding:'2px 0', borderTop:'1px solid #f0f0f0' }}>
          <span style={{ fontWeight:600 }}>{p.referencia}</span>
          {p.empresa && <span style={{ color:'#888' }}> · {p.empresa}</span>}
          {p.presupuesto > 0 && <span style={{ float:'right', color:'#15803d' }}>${fmt(p.presupuesto)}</span>}
        </div>
      ))}
      {d.payload.listaProyectos?.length > 8 && (
        <div style={{ fontSize:10, color:'#aaa', marginTop:4 }}>+{d.payload.listaProyectos.length-8} más...</div>
      )}
    </div>
  );
};

// ── ACTIVE SHAPE PIE (render label en el arco activo) ────────────────────────
const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;
  return (
    <g>
      <text x={cx} y={cy-8} textAnchor="middle" fill={fill} style={{ fontSize:13, fontWeight:700 }}>{payload.name}</text>
      <text x={cx} y={cy+12} textAnchor="middle" fill="#666" style={{ fontSize:11 }}>{payload.mainValue}</text>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius+8} startAngle={startAngle} endAngle={endAngle} fill={fill}/>
    </g>
  );
};

// ── PIE CARD ─────────────────────────────────────────────────────────────────
function PieCard({ title, data, height=280 }) {
  const [activeIdx, setActiveIdx] = useState(null);
  return (
    <div className="card">
      <h4 style={{ fontSize:14, fontWeight:700, marginBottom:12, color:'#555' }}>{title}</h4>
      {data.length === 0 ? (
        <div style={{ textAlign:'center', padding:40, color:'#ccc', fontSize:13 }}>Sin datos</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie
                data={data}
                cx="50%" cy="50%"
                innerRadius={55} outerRadius={90}
                dataKey="value"
                activeIndex={activeIdx}
                activeShape={renderActiveShape}
                onMouseEnter={(_, i) => setActiveIdx(i)}
                onMouseLeave={() => setActiveIdx(null)}
              >
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.color} stroke="white" strokeWidth={2}/>
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip/>}/>
            </PieChart>
          </ResponsiveContainer>
          {/* Leyenda */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 12px', marginTop:8, justifyContent:'center' }}>
            {data.map((d,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11 }}>
                <div style={{ width:10, height:10, borderRadius:2, background:d.color, flexShrink:0 }}/>
                <span style={{ color:'#666' }}>{d.name}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function AdminPage() {
  const { user, isMaster } = useAuth();
  const puedeGestionarUsuarios = isMaster || ['admin', 'gerente_financiera'].includes(user?.rol);
  const [tab, setTab] = useState('metricas');
  const [usuarios, setUsuarios] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ userId:'', desde:'', hasta:'' });
  const [editUser, setEditUser] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [newUser, setNewUser] = useState(null);
  const [savingNewUser, setSavingNewUser] = useState(false);
  const emptyNewUser = { nombre:'', apellido:'', email:'', cargo:'', telefono:'', rol:'usuario' };

  const handleExportarBD = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/export/database', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `odin_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al exportar la base de datos');
    } finally {
      setExporting(false);
    }
  };

  // Assign persistent colors to entities
  const getColor = useCallback((name, index, useUserColor, users) => {
    if (useUserColor) {
      const u = users?.find(u => `${u.nombre} ${u.apellido}` === name);
      if (u?.colorAsesor) return u.colorAsesor;
    }
    return PALETTE[index % PALETTE.length];
  }, []);

  const load = async (params = filtros) => {
    try {
      const [uRes, dRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/dashboard/admin', { params })
      ]);
      setUsuarios(uRes.data);
      setDashboard(dRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const applyFiltros = () => { setLoading(true); load(filtros); };
  const limpiar = () => { const f={userId:'',desde:'',hasta:''}; setFiltros(f); load(f); };

  const handleCreateUser = async () => {
    if (!newUser.nombre.trim() || !newUser.apellido.trim() || !newUser.email.trim()) {
      toast.error('Nombre, apellido y correo son requeridos'); return;
    }
    setSavingNewUser(true);
    try {
      await api.post('/admin/users', newUser);
      toast.success('Usuario creado — le llegó un correo para configurar su contraseña');
      setNewUser(null); await load();
    } catch(err) { toast.error(err.response?.data?.error||'Error al crear usuario'); }
    finally { setSavingNewUser(false); }
  };

  const handleUpdateUser = async () => {
    try {
      await api.put(`/admin/users/${editUser.id}`, { rol: editUser.rol, activo: editUser.activo });
      toast.success('Usuario actualizado');
      setEditUser(null); await load();
    } catch(err) { toast.error(err.response?.data?.error||'Error'); }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Eliminar a ${u.nombre} ${u.apellido}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success('Eliminado'); await load(); }
    catch(err) { toast.error(err.response?.data?.error||'Error'); }
  };

  const handleColorChange = async (userId, color) => {
    try {
      await api.put(`/admin/users/${userId}`, { colorAsesor: color });
      // Update local usuarios list
      setUsuarios(prev => prev.map(u => u.id === userId ? { ...u, colorAsesor: color } : u));
      toast.success('Color actualizado');
    } catch { toast.error('Error al guardar color'); }
  };

  const badgeRol = (rol) => {
    const map = { master:'badge-red', admin:'badge-blue', gerente_financiera:'badge-orange', gerente_proyectos:'badge-yellow', usuario:'badge-gray', tecnico:'badge-green' };
    const label = { master:'Master', admin:'Admin', gerente_financiera:'Ger. Financiera', gerente_proyectos:'Ger. Proyectos', usuario:'Usuario', tecnico:'Técnico' };
    return <span className={`badge ${map[rol]||'badge-gray'}`}>{label[rol]||rol}</span>;
  };

  // ── Build chart data ────────────────────────────────────────────────────────
  const modoAsesor = !!filtros.userId;
  const presTotal = parseFloat(dashboard?.financiero?.presupuestoTotal||0);

  // When specific asesor → show empresa pies
  const empresaData = (dashboard?.metricasPorEmpresa||[]).map((m,i) => ({
    name: m.empresa,
    color: PALETTE[i % PALETTE.length],
    // Pie 1: proyectos por empresa
    proyectos: { value: m.proyectos, mainValue: `${m.proyectos} proyecto(s)`, listaProyectos: m.listaProyectos },
    // Pie 2: presupuesto por empresa $
    presupuesto: { value: m.presupuestoTotal, mainValue: `$${fmt(m.presupuestoTotal)}`, listaProyectos: m.listaProyectos },
    // Pie 3: % ventas
    pctVentas: { value: presTotal>0?(m.presupuestoTotal/presTotal*100):0, mainValue: presTotal>0?`${(m.presupuestoTotal/presTotal*100).toFixed(1)}% del total`:'0%', listaProyectos: m.listaProyectos },
    // Pie 4: utilidad
    utilidad: { value: m.utilidadTotal, mainValue: `$${fmt(m.utilidadTotal)} (${presTotal>0?(m.utilidadTotal/presTotal*100).toFixed(1):0}%)`, listaProyectos: m.listaProyectos },
  }));

  // When all asesores → show asesor pies  
  const asesorData = (dashboard?.metricasPorUsuario||[]).map((m,i) => {
    const nombre = `${m.usuario?.nombre||''} ${m.usuario?.apellido||''}`.trim();
    const color = m.usuario?.colorAsesor || PALETTE[i % PALETTE.length];
    return {
      name: nombre, color,
      proyectos: { value: m.proyectos, mainValue: `${m.proyectos} proyecto(s)`, listaProyectos: m.listaProyectos },
      presupuesto: { value: m.presupuestoTotal, mainValue: `$${fmt(m.presupuestoTotal)}`, listaProyectos: m.listaProyectos },
      pctVentas: { value: presTotal>0?(m.presupuestoTotal/presTotal*100):0, mainValue: presTotal>0?`${(m.presupuestoTotal/presTotal*100).toFixed(1)}% del total`:'0%', listaProyectos: m.listaProyectos },
      utilidad: { value: m.utilidadTotal, mainValue: `$${fmt(m.utilidadTotal)} (${presTotal>0?(m.utilidadTotal/presTotal*100).toFixed(1):0}%)`, listaProyectos: m.listaProyectos },
    };
  });

  const activeData = modoAsesor ? empresaData : asesorData;

  const makePie = (key) => activeData.map(d => ({
    name: d.name, color: d.color,
    value: d[key].value,
    mainValue: d[key].mainValue,
    listaProyectos: d[key].listaProyectos,
  }));

  const chartData = (dashboard?.metricasPorUsuario||[]).map(m => ({
    name: `${m.usuario?.nombre||''} ${m.usuario?.apellido||''}`.trim(),
    color: m.usuario?.colorAsesor || '#7B0D0D',
    presupuesto: m.presupuestoTotal||0,
    utilidad: m.utilidadTotal||0,
    proyectos: m.proyectos||0,
  }));

  const asesorSelNombre = filtros.userId
    ? usuarios.find(u => u.id === filtros.userId)
    : null;
  const asesorMetrica = filtros.userId
    ? dashboard?.metricasPorUsuario?.find(m => m.usuario?.id === filtros.userId)
    : null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Panel de Administracion</h1>
          <p className="page-subtitle">Metricas globales y gestion de usuarios</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:24, background:'white', padding:4, borderRadius:10, width:'fit-content', boxShadow:'var(--sombra)' }}>
        {[{key:'metricas',label:'Metricas'},{key:'usuarios',label:'Usuarios'},{key:'exportar',label:'Exportar'}].map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{ padding:'8px 20px', borderRadius:8, border:'none', cursor:'pointer', fontSize:14, fontWeight:500, background:tab===t.key?'#7B0D0D':'transparent', color:tab===t.key?'white':'#666', transition:'all 0.2s' }}>
            {t.label}
          </button>))}
      </div>

      {/* ── METRICAS ── */}
      {tab === 'metricas' && (
        <div>
          {/* Filtros */}
          <div className="card" style={{ marginBottom:20 }}>
            <h3 className="card-title" style={{ marginBottom:14 }}>Filtrar metricas</h3>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
              <div className="form-group" style={{ margin:0, flex:1, minWidth:180 }}>
                <label className="form-label">Por asesor</label>
                <select className="form-select" value={filtros.userId} onChange={e=>setFiltros({...filtros,userId:e.target.value})}>
                  <option value="">Todos los asesores</option>
                  {usuarios.map(u=>(
                    <option key={u.id} value={u.id}>{u.nombre} {u.apellido}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Desde</label>
                <input className="form-input" type="date" value={filtros.desde} onChange={e=>setFiltros({...filtros,desde:e.target.value})}/>
              </div>
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Hasta</label>
                <input className="form-input" type="date" value={filtros.hasta} onChange={e=>setFiltros({...filtros,hasta:e.target.value})}/>
              </div>
              <button className="btn btn-primary" onClick={applyFiltros}>Aplicar filtros</button>
              <button className="btn btn-secondary" onClick={limpiar}>Limpiar</button>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign:'center', padding:40 }}><div className="spinner spinner-dark" style={{ width:30, height:30, margin:'0 auto' }}/></div>
          ) : dashboard && (<>

            {/* KPIs */}
            <div className="grid-4" style={{ marginBottom:20 }}>
              {[
                { label:'Usuarios activos', value: dashboard.resumen.totalUsuarios },
                { label:'Total clientes', value: dashboard.resumen.totalClientes },
                { label: filtros.userId ? `Proyectos de ${asesorSelNombre?.nombre||''}` : 'Total proyectos', value: dashboard.resumen.totalProyectos },
                { label:'Margen general', value:`${dashboard.financiero.margenGeneral}%`, highlight:true },
              ].map(item=>(
                <div key={item.label} className="card" style={{ textAlign:'center' }}>
                  <div style={{ fontSize:32, fontWeight:800, color:item.highlight?'#16a34a':'#7B0D0D' }}>{item.value}</div>
                  <div style={{ fontSize:13, color:'#888', marginTop:4 }}>{item.label}</div>
                </div>
              ))}
            </div>

            {/* Financiero principal */}
            <div className="grid-3" style={{ marginBottom:20 }}>
              {[
                { label:'P Val — Presupuesto total (sin IVA)', value: dashboard.financiero.presupuestoTotal, color:'#7B0D0D', sub:'Excl. dados de baja' },
                { label:'Costos', value: dashboard.financiero.costoTotal, color:'#d97706' },
                { label:'Utilidad (sin IVA)', value: dashboard.financiero.utilidadTotal, color:'#16a34a' },
              ].map(item=>(
                <div key={item.label} className="card" style={{ borderTop:`4px solid ${item.color}` }}>
                  <div style={{ fontSize:13, color:'#888', marginBottom:6 }}>{item.label}</div>
                  <div style={{ fontSize:26, fontWeight:800, color:item.color }}>${fmt(item.value)}</div>
                  {item.sub && <div style={{ fontSize:11, color:'#aaa', marginTop:4 }}>{item.sub}</div>}
                </div>
              ))}
            </div>

            {/* Asesor especifico: datos extra */}
            {filtros.userId && asesorMetrica && (
              <div style={{ padding:'12px 16px', background:'#f0f9ff', borderRadius:10, border:'1px solid #bae6fd', marginBottom:20, fontSize:13 }}>
                <strong style={{ color:'#0369a1' }}>{asesorSelNombre?.nombre} {asesorSelNombre?.apellido}</strong>
                <span style={{ color:'#555', marginLeft:12 }}>Clientes: <strong>{asesorMetrica.listaProyectos?.length || 0}</strong></span>
                <span style={{ color:'#555', marginLeft:12 }}>Proyectos: <strong>{asesorMetrica.proyectos}</strong></span>
                <span style={{ color:'#15803d', marginLeft:12 }}>Margen: <strong>{asesorMetrica.presupuestoTotal > 0 ? ((asesorMetrica.utilidadTotal/asesorMetrica.presupuestoTotal)*100).toFixed(1) : 0}%</strong></span>
              </div>
            )}

            {/* 4 PASTELES */}
            {activeData.length > 0 && (
              <>
                <div style={{ marginBottom:12, fontSize:14, fontWeight:600, color:'#555' }}>
                  {modoAsesor
                    ? `Distribucion por empresa — ${asesorSelNombre?.nombre} ${asesorSelNombre?.apellido}`
                    : 'Distribucion por asesor — todos'}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }}>
                  <PieCard
                    title={modoAsesor ? 'Proyectos por empresa' : 'Proyectos por asesor'}
                    data={makePie('proyectos')}/>
                  <PieCard
                    title={modoAsesor ? 'Presupuesto por empresa ($, sin IVA)' : 'Presupuesto por asesor ($, sin IVA)'}
                    data={makePie('presupuesto')}/>
                  <PieCard
                    title={modoAsesor ? '% de ventas por empresa' : '% de ventas por asesor'}
                    data={makePie('pctVentas')}/>
                  <PieCard
                    title={modoAsesor ? 'Utilidad por empresa ($, sin IVA)' : 'Utilidad por asesor ($, sin IVA)'}
                    data={makePie('utilidad')}/>
                </div>
              </>
            )}

            {/* Barras por asesor (cuando no hay filtro de asesor) */}
            {!filtros.userId && chartData.length > 0 && (
              <div className="card" style={{ marginBottom:24 }}>
                <h3 className="card-title" style={{ marginBottom:16 }}>Presupuesto vs Utilidad por asesor (sin IVA)</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData} margin={{ left:-10 }}>
                    <XAxis dataKey="name" tick={{ fontSize:12 }}/>
                    <YAxis tick={{ fontSize:11 }} tickFormatter={fmtK}/>
                    <Tooltip formatter={v=>`$${fmt(v)}`}/>
                    <Bar dataKey="presupuesto" name="Presupuesto" radius={[4,4,0,0]}>
                      {chartData.map((d,i)=><Cell key={i} fill={d.color}/>)}
                    </Bar>
                    <Bar dataKey="utilidad" name="Utilidad" radius={[4,4,0,0]}>
                      {chartData.map((d,i)=><Cell key={i} fill={d.color} fillOpacity={0.5}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Tabla por asesor */}
            <div className="card">
              <h3 className="card-title" style={{ marginBottom:16 }}>Detalle por asesor</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Color</th><th>Asesor</th><th>Proyectos</th><th>Activos</th>
                      <th>Finalizados</th><th>Presupuesto total (sin IVA)</th><th>Utilidad (sin IVA)</th><th>Margen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.metricasPorUsuario.length === 0 ? (
                      <tr><td colSpan={8} style={{ textAlign:'center', color:'#888', padding:24 }}>Sin datos</td></tr>
                    ) : dashboard.metricasPorUsuario.map(m => {
                      const margen = m.presupuestoTotal > 0 ? ((m.utilidadTotal/m.presupuestoTotal)*100).toFixed(1) : 0;
                      const color = m.usuario?.colorAsesor || '#7B0D0D';
                      return (
                        <tr key={m.usuario?.id}>
                          <td>
                            <div style={{ width:20, height:20, borderRadius:4, background:color, margin:'0 auto' }}/>
                          </td>
                          <td><strong>{m.usuario?.nombre} {m.usuario?.apellido}</strong></td>
                          <td style={{ fontWeight:600 }}>{m.proyectos}</td>
                          <td><span className="badge badge-blue">{m.activos}</span></td>
                          <td><span className="badge badge-green">{m.finalizados}</span></td>
                          <td style={{ fontWeight:600 }}>${fmt(m.presupuestoTotal)}</td>
                          <td style={{ fontWeight:600, color:'#16a34a' }}>${fmt(m.utilidadTotal)}</td>
                          <td><span className={`badge ${parseFloat(margen)>=20?'badge-green':parseFloat(margen)>=10?'badge-yellow':'badge-red'}`}>{margen}%</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>)}
        </div>
      )}

      {/* ── USUARIOS ── */}
      {tab === 'usuarios' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Gestion de usuarios ({usuarios.length})</h3>
            {puedeGestionarUsuarios && (
              <button className="btn btn-primary btn-sm" onClick={()=>setNewUser({...emptyNewUser})}>+ Nuevo usuario</button>
            )}
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Color</th><th>Usuario</th><th>Correo</th><th>Cargo</th>
                  <th>Rol</th><th>Estado</th><th>Registro</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <input type="color" value={u.colorAsesor||'#7B0D0D'}
                          onChange={e => handleColorChange(u.id, e.target.value)}
                          style={{ width:28, height:28, border:'none', padding:0, cursor:'pointer', borderRadius:4 }}
                          title="Color del asesor (aparece en agenda y metricas)"/>
                      </div>
                    </td>
                    <td><strong>{u.nombre} {u.apellido}</strong></td>
                    <td style={{ fontSize:13 }}>{u.email}</td>
                    <td style={{ fontSize:13 }}>{u.cargo||'—'}</td>
                    <td>{badgeRol(u.rol)}</td>
                    <td><span className={`badge ${u.activo?'badge-green':'badge-red'}`}>{u.activo?'Activo':'Inactivo'}</span></td>
                    <td style={{ fontSize:13, color:'#888' }}>{new Date(u.createdAt).toLocaleDateString('es-EC')}</td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={()=>setEditUser({...u})}>Editar rol</button>
                        {isMaster && u.rol !== 'master' && (
                          <button className="btn btn-danger btn-sm" onClick={()=>handleDeleteUser(u)}>Eliminar</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding:'10px 14px', background:'#fffbeb', borderRadius:8, margin:'12px 0', fontSize:12, color:'#92400e', border:'1px solid #fef3c7' }}>
            Haz clic en el cuadro de color de cada asesor para asignarle su color. Este color aparecera en la agenda compartida y en las graficas de metricas.
          </div>
        </div>
      )}

      {tab === 'exportar' && (
        <div className="card" style={{ maxWidth:560 }}>
          <div className="card-header">
            <h3 className="card-title">Exportar base de datos completa</h3>
          </div>
          <p style={{ color:'#555', fontSize:13, marginBottom:16 }}>
            Descarga un archivo Excel con todas las tablas del sistema (clientes, proyectos, cotizaciones, obras, gastos, etc.),
            listo para importar en PowerBI y armar tus propios reportes y gráficos.
          </p>
          <button className="btn btn-primary" onClick={handleExportarBD} disabled={exporting}>
            {exporting ? <><span className="spinner"/> Generando...</> : 'Descargar base de datos (Excel)'}
          </button>
        </div>
      )}

      {/* Modal editar usuario */}
      {editUser && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:400 }}>
            <div className="modal-header">
              <h3 className="modal-title">Editar usuario</h3>
              <button className="btn-icon" onClick={()=>setEditUser(null)}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom:16, color:'#555' }}>
                <strong>{editUser.nombre} {editUser.apellido}</strong><br/>
                <span style={{ fontSize:13, color:'#888' }}>{editUser.email}</span>
              </p>
              <div className="form-group">
                <label className="form-label">Rol</label>
                <select className="form-select" value={editUser.rol} disabled={editUser.rol==='master' && !isMaster}
                  onChange={e=>setEditUser({...editUser,rol:e.target.value})}>
                  <option value="usuario">Usuario / Asesor</option>
                  <option value="tecnico">Tecnico</option>
                  <option value="gerente_financiera">Gerente Financiera</option>
                  <option value="gerente_proyectos">Gerente de Proyectos</option>
                  <option value="admin">Administrador</option>
                  {isMaster && <option value="master">Master</option>}
                </select>
                {editUser.rol==='master' && !isMaster && (
                  <span style={{ fontSize:11, color:'#888', marginTop:4, display:'block' }}>
                    Solo otro Master puede cambiar el rol de una cuenta Master.
                  </span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Estado de la cuenta</label>
                <select className="form-select" value={editUser.activo?'true':'false'} onChange={e=>setEditUser({...editUser,activo:e.target.value==='true'})}>
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setEditUser(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleUpdateUser}>Guardar cambios</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nuevo usuario */}
      {newUser && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:440 }}>
            <div className="modal-header">
              <h3 className="modal-title">Nuevo usuario</h3>
              <button className="btn-icon" onClick={()=>setNewUser(null)}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom:16, color:'#888', fontSize:13 }}>
                Le va a llegar un correo a la persona para que configure su propia contraseña — tú no la eliges.
              </p>
              <div className="form-row form-row-2">
                <div className="form-group">
                  <label className="form-label">Nombre</label>
                  <input className="form-input" value={newUser.nombre} onChange={e=>setNewUser({...newUser,nombre:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Apellido</label>
                  <input className="form-input" value={newUser.apellido} onChange={e=>setNewUser({...newUser,apellido:e.target.value})}/>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Correo</label>
                <input className="form-input" type="email" value={newUser.email} onChange={e=>setNewUser({...newUser,email:e.target.value})}/>
              </div>
              <div className="form-row form-row-2">
                <div className="form-group">
                  <label className="form-label">Cargo</label>
                  <input className="form-input" value={newUser.cargo} onChange={e=>setNewUser({...newUser,cargo:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input className="form-input" value={newUser.telefono} onChange={e=>setNewUser({...newUser,telefono:e.target.value})}/>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Rol</label>
                <select className="form-select" value={newUser.rol} onChange={e=>setNewUser({...newUser,rol:e.target.value})}>
                  <option value="usuario">Usuario / Asesor</option>
                  <option value="tecnico">Tecnico</option>
                  <option value="gerente_financiera">Gerente Financiera</option>
                  <option value="gerente_proyectos">Gerente de Proyectos</option>
                  <option value="admin">Administrador</option>
                  {isMaster && <option value="master">Master</option>}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setNewUser(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleCreateUser} disabled={savingNewUser}>
                {savingNewUser ? <><span className="spinner"/> Creando...</> : 'Crear usuario'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
